import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "./AnnouncementBanner.module.css";

interface AnnouncementBannerProps {
  message: string;
  mode?: "informational" | "emergency";
  onDismiss?: () => void;
  preview?: boolean;
  prominentDismiss?: boolean;
  /**
   * TRAC-862 — optional primary action in emergency mode (e.g. "Talk to a
   * person"). Purely additive: both existing trigger paths pass neither prop and
   * render exactly as before. Rendered only when BOTH are supplied, so a label
   * without a handler can't produce a dead button on a screen whose entire job
   * is telling the customer what still works.
   */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * TRAC-1052 — a small tester-only line on the emergency overlay, set only
   * when the overlay is this browser's own outage preview, so nobody mistakes
   * it for a real outage. Absent on every real overlay.
   */
  testerNote?: string;
}

/**
 * AnnouncementBanner - Unified announcement display
 *
 * Two modes:
 * - informational: Closable yellow banner at top of chat (maintenance notices, etc.)
 * - emergency: Full-screen takeover, not closable, disables all interaction
 *
 * ──────────────────────────────────────────────────────────────────────────
 * LOAD-BEARING COMPONENT — READ BEFORE MODIFYING (TRAC-459)
 *
 * The emergency mode of this component is the USER-FACING end of an
 * auto-maintenance mechanism with TWO trigger paths. Both paths MUST keep
 * working if you rewrite or replace this component.
 *
 * PATH 1 — Server-side auto-engage (the normal path):
 *   A CloudWatch health alarm fires (`deep-health-unhealthy` or
 *   `agentcore-unhealthy`), a Lambda writes `auto_maintenance.enabled = true`
 *   to the DynamoDB `global-settings` table, the backend merges that flag
 *   into `GET /api/settings/announcement` and returns
 *   `{ enabled: true, mode: "emergency", message }`, and this component
 *   renders the overlay. Handles the "backend alive, some dep is sick" case.
 *
 * PATH 2 — Client-side fallback (the backstop):
 *   If the announcement endpoint itself is unreachable for ~60 seconds
 *   (2 consecutive 5xx / network / timeout errors while navigator.onLine is
 *   true), the ChatInterface polling logic flips local state to emergency
 *   mode using a hardcoded fallback message and this component renders the
 *   overlay the same way. Handles the "backend totally dead" case that
 *   PATH 1 can't signal by definition. Admin-toggleable via the
 *   `client_fallback_overlay` feature flag.
 *
 * If you replace this component, the new version MUST still render a
 * full-screen blocking overlay when it receives `mode === "emergency"` and
 * a non-empty `message`, regardless of which path produced the state.
 * Breaking either path silently disables a safety mechanism — users
 * will keep hitting errors on a broken system with no warning.
 *
 * You can freely restyle, rewrite in another framework, or split this
 * into multiple components. Just preserve the contract with the state
 * that ChatInterface sets. Run the frontend unit tests after any change —
 * they cover this contract explicitly for both paths.
 *
 * Related: `backend/src/routers/settings.py` (merge logic, fallback flag),
 * `infrastructure/lambda/health-alert-notifier/index.py` (DDB writer),
 * `frontend/src/components/chat/ChatInterface.tsx` (poll + fallback logic).
 * ──────────────────────────────────────────────────────────────────────────
 */
const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  message,
  mode = "informational",
  onDismiss,
  preview = false,
  prominentDismiss = false,
  actionLabel,
  onAction,
  testerNote,
}) => {
  if (!message) {
    return null;
  }

  if (mode === "emergency") {
    return (
      <div className={`${styles.emergencyOverlay}${preview ? ` ${styles.emergencyPreview}` : ''}`}>
        {testerNote && (
          <div className={styles.testerNote} data-testid="outage-preview-note">
            {testerNote}
          </div>
        )}
        <div className={styles.emergencyCard}>
          <div className={styles.emergencyIcon}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className={styles.emergencyContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.emergencyLink}
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {message}
            </ReactMarkdown>
          </div>
          {actionLabel && onAction && (
            <button
              className={styles.emergencyAction}
              onClick={onAction}
              type="button"
            >
              {actionLabel}
            </button>
          )}
          {onDismiss && prominentDismiss && (
            <button
              className={styles.emergencyProminentDismiss}
              onClick={onDismiss}
              aria-label="Dismiss announcement"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    );
  }

  // Informational mode — closable sticky banner
  return (
    <div className={`${styles.banner}${preview ? ` ${styles.bannerPreview}` : ''}`}>
      <div className={styles.iconWrapper}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <div className={styles.content}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {children}
              </a>
            ),
            p: ({ children }) => <span className={styles.paragraph}>{children}</span>,
          }}
        >
          {message}
        </ReactMarkdown>
      </div>
      {onDismiss && (
        <button
          className={styles.dismissButton}
          onClick={onDismiss}
          aria-label="Dismiss announcement"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default AnnouncementBanner;
